import { isDevMode } from '@angular/core';

export const AGE_MAJORITE = 18;
export const TYPING_DEBOUNCE_TIME = 300;

export const MONTANT_NEGATIVE_MIN = -2000000000;
export const MONTANT_MIN = 0;
export const MONTANT_MAX = 2000000000;

export const CATEGORIE_SOCIO_PROFESSIONNELLE_PARENT_CODE_ATLAS_RETRAITE = 7;

export const TOOLTIP_DEFAULT = 'Copier';
export const TOOLTIP_COPIED = 'Copié';

export function isMontantOutOfRange(value: number): boolean {
  return value < MONTANT_MIN || value > MONTANT_MAX;
}

export function isMontantNegativeOutOfRange(value: number): boolean {
  return value < MONTANT_NEGATIVE_MIN || value > MONTANT_MAX;
}

const parsePositiveNumberOrNull = (
  value: string | number | null | undefined,
  parser: (value: string) => number,
  postProcess?: (num: number) => number
): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const num = parser(value.toString());
  if (isNaN(num)) {
    return null;
  }

  const processedNum = postProcess ? postProcess(num) : num;
  if (processedNum && isMontantOutOfRange(processedNum)) {
    return null;
  }

  return processedNum;
};

const parseNumberAllowNegativeOrNull = (
  value: string | number | null | undefined,
  parser: (value: string) => number,
  postProcess?: (num: number) => number
): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const num = parser(value.toString());
  if (isNaN(num)) {
    return null;
  }

  const processedNum = postProcess ? postProcess(num) : num;
  if (processedNum && isMontantNegativeOutOfRange(processedNum)) {
    return null;
  }

  return processedNum;
};

export const toIntegerOrNull = (value: string | number | null | undefined): number | null => {
  return parsePositiveNumberOrNull(value, Number, num => Math.trunc(num));
};

export const toFloatOrNull = (value: string | number | null | undefined): number | null => {
  return parsePositiveNumberOrNull(value, parseFloat, num => parseFloat(num.toFixed(2)));
};

export const toNumberOrNull = (value: string | number | null | undefined): number | null => {
  return parsePositiveNumberOrNull(value, Number);
};

export const toNumberAllowNegativeOrNull = (value: string | number | null | undefined): number | null => {
  return parseNumberAllowNegativeOrNull(value, Number);
};

export function toDateOrUndefined(d?: any): Date | undefined {
  if (!d) return undefined;
  const date = new Date(d);
  return Number.isNaN(date.getTime()) ? undefined : date;
}

export function toTrimmedStringOrUndefined(value?: any): string | undefined {
  if (value === null || value === undefined) {
    return undefined;
  }
  if (typeof value !== 'string') {
    return value;
  }
  return value.trim();
}

export const lettersRegex = "^[a-zA-ZÀ-ÿ '\\-]*$";
export const adresseRegex = "^[A-Za-zÀ-ÖØ-öø-ÿ0-9'’,.\\-\\/ éë]*$";
export const numbersRegex = '^[0-9]*$';
export const postCodeRegex = '^[A-Za-z0-9 .-]+$';
export const numbersAndSeparatorsRegex = '^[0-9/-]*$';

export const EMOJI_REGEX = /\p{Extended_Pictographic}/gu;

export function removeEmojis(value: string): string {
  if (!value) return value;
  return value.replace(EMOJI_REGEX, '');
}

export const removeAccents = (string?: string): string => {
  if (!string) {
    return '';
  }
  return string
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
};

// Convertit un libellé en partie de nom de fichier : sans accents ni caractères spéciaux (dont « - », séparateur des noms de fichiers GED),
// mots séparés par des underscores
export const toFileNamePart = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim()
    .replace(/\s+/g, '_');

export const capitalizeFirstLetter = (value: string): string => {
  if (!value) return '';
  const lower = value.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};

export const emptyToUndefined = (v?: string | null) => (v && v.trim() !== '' ? v : undefined);

export const yesterdayDate = () => new Date(new Date().setDate(new Date().getDate() - 1));

export const currentDatePivotYear = () => new Date().getFullYear() % 100;

export function isMinor(birthInput?: any, nowInput: any = new Date()): boolean {
  const age = agePersonne(birthInput, nowInput);

  if (age === undefined) return false;

  return age < 18;
}

export function agePersonne(birthInput?: any, nowInput: any = new Date()): number | undefined {
  const birth = toDateOrUndefined(birthInput);
  const now = toDateOrUndefined(nowInput);

  if (!birth || !now) return undefined;

  const birthDate = new Date(birth.getFullYear(), birth.getMonth(), birth.getDate());

  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  let age = today.getFullYear() - birthDate.getFullYear();

  const birthdayThisYear = new Date(today.getFullYear(), birthDate.getMonth(), birthDate.getDate());

  if (today < birthdayThisYear) {
    age--;
  }

  return age;
}

export const devLog = (...args: unknown[]): void => {
  if (isDevMode()) {
    console.log(...args);
  }
};

export function sortByCustomOrder<T>(array: T[], order: string[], key: keyof T): T[] {
  const orderMap = new Map<string, number>(order.map((value, index) => [value, index]));

  return [...array].sort((a, b) => {
    const indexA = orderMap.get(String(a[key]));
    const indexB = orderMap.get(String(b[key]));

    if (indexA === undefined && indexB === undefined) return 0;
    if (indexA === undefined) return 1;
    if (indexB === undefined) return -1;

    return indexA - indexB;
  });
}

export function getRawFileId(fileId: string | undefined | null): string {
  return fileId?.split('#')?.[0] ?? '';
}
